import {
    ChangeDetectionStrategy,
    Component,
    Directive,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
    NgControl,
    ReactiveFormsModule
} from '@angular/forms';
import { UserSettingsQuery } from 'src/app/features/user-settings/state/user-settings.query';
import { sv } from 'date-fns/locale';
import {
    defaultLocale,
    supportedLocales
} from '../../../../features/settings/state/settings.store';
import { startWith, takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import {
    addMonths,
    addQuarters,
    addYears,
    differenceInQuarters,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    isFirstDayOfMonth,
    isLastDayOfMonth,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subMonths,
    subQuarters,
    subYears
} from 'date-fns';
import { TablerIconsModule } from 'angular-tabler-icons';
import { filterNilValue } from '@datorama/akita';
import { CustomDateAdapter } from '@utils';

export interface DateParse {
    dateInput: string;
}
export type DateDisplay = DateParse & {
    monthYearLabel?: string;
    dateA11yLabel?: string;
    monthYearA11yLabel?: string;
};
export class CustomDateFormat {
    private _parse: DateParse = {
        dateInput: 'YYYY/MM/DD'
    };
    private _display: DateDisplay = {
        dateInput: 'YYYY/MM/DD',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMM YYYY'
    };

    set parse(parse: DateParse) {
        this._parse = Object.assign({}, this._parse, parse);
    }

    get parse(): DateParse {
        return this._parse;
    }

    set display(display: DateDisplay) {
        this._display = Object.assign({}, this._display, display);
    }

    get display(): DateDisplay {
        return this._display;
    }

    updateDateFormat(parse: DateParse, display?: DateDisplay) {
        this.parse = parse;
        if (!display) {
            display = parse;
        }
        this.display = display;
    }
}
@Directive({
    selector: '[datePickerFormat]',
    providers: [
        {
            provide: MAT_DATE_FORMATS,
            useClass: CustomDateFormat
        }
    ],
    standalone: true
})
export class DatePickerFormatDirective {
    @Input() public configDateParse: DateParse;
    @Input() public configDateDisplay: DateDisplay;

    @Input('datePickerFormat')
    set datePickerFormat(format: string) {
        if (this.configDateParse) {
            this.matDateFormat.updateDateFormat(
                this.configDateParse,
                this.configDateDisplay
            );
        } else {
            this.matDateFormat.updateDateFormat({ dateInput: format });
        }
        // We need this for the first time to tell component change new format
        const value = this.ngControl.value;
        this.ngControl.valueAccessor?.writeValue(value);
    }

    constructor(
        @Inject(MAT_DATE_FORMATS) public matDateFormat: CustomDateFormat,
        @Optional() private ngControl: NgControl
    ) {}
}

export interface PeriodConfig {
    fromDate: Date;
    toDate: Date;
    periodMode?: 'month' | 'quarter' | 'year' | 'custom';
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatDateFnsModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        ReactiveFormsModule,
        DatePickerFormatDirective,
        TablerIconsModule
    ],
    providers: [
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: sv
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SbPeriodPickerComponent,
            multi: true
        }
    ],
    selector: 'sb-period-picker',
    standalone: true,
    styleUrls: ['./sb-period-picker.component.scss'],
    templateUrl: './sb-period-picker.component.html'
})
export class SbPeriodPickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
    get label(): string {
        return this._label;
    }

    @Input() set label(value: string) {
        this._label = value;
    }
    constructor(
        private _adapter: DateAdapter<any>,
        private _userSettingsQuery: UserSettingsQuery
    ) {
        this._adapter.setLocale(sv);
        this._userSettingsQuery
            .selectGlobalSetting$('LOCALE')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((culture) => {
                let locale = culture.substring(0, culture.indexOf('-')) || culture;
                // check support for locale...
                if (supportedLocales.indexOf(locale) > -1) {
                    if (locale === 'en') {
                        locale = 'en-US';
                    }
                    // ...and load it if supported
                    return import(
                        /* webpackInclude: /(no|nb|sv|en-GB|en-US|fi|da)\/index\.js$/ */
                        `date-fns/locale/${locale}`
                    ).then((module) => this._adapter.setLocale(module.default));
                } else {
                    // ...or load default locale
                    return import(
                        /* webpackInclude: /(no|nb|sv|en-GB|en-US|fi|da)\/index\.js$/ */
                        `date-fns/locale/${defaultLocale}`
                    ).then((module) => this._adapter.setLocale(module.default));
                }
            });
    }
    unsubscribe$ = new Subject();
    periods = [
        {
            label: 'Month',
            value: 'month'
        },
        {
            label: 'Quarter',
            value: 'quarter'
        },
        {
            label: 'Year',
            value: 'year'
        },
        {
            label: 'Custom',
            value: 'custom'
        }
    ] as const;
    private _label = 'View by';
    periodCtrl = new FormControl<(typeof this.periods)[number]['value']>('month');
    dateCtrl = new FormControl<Date>(null);
    fromDateCtrl = new FormControl<Date>(null);
    toDateCtrl = new FormControl<Date>(null);
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    writeValue(config: PeriodConfig): void {
        if (config && config.periodMode) {
            this.fromDateCtrl.setValue(config.fromDate || new Date());
            this.toDateCtrl.setValue(config.toDate || new Date());
            this.dateCtrl.setValue(config.fromDate || new Date());
            this.periodCtrl.patchValue(config.periodMode);
        } else if (config) {
            // Calculate period mode based on from and to date
            if (isFirstDayOfMonth(config.fromDate) && isLastDayOfMonth(config.toDate)) {
                const quarters = differenceInQuarters(config.toDate, config.fromDate, {
                    roundingMethod: 'ceil'
                });
                if (quarters === 1) {
                    this.periodCtrl.patchValue('quarter', { emitEvent: false });
                } else if (quarters < 1) {
                    this.periodCtrl.patchValue('month', { emitEvent: false });
                } else {
                    this.periodCtrl.patchValue('year', { emitEvent: false });
                }
            } else {
                this.periodCtrl.patchValue('custom', { emitEvent: false });
            }

            this.fromDateCtrl.setValue(config.fromDate || new Date());
            this.toDateCtrl.setValue(config.toDate || new Date());
            this.dateCtrl.setValue(config.fromDate || new Date());
        }
    }
    onChange = (dates) => {};

    onTouched = () => {};

    /** setMonthAndYear - set from and to date to start and end of month for passed date
     * @param date - date to derive year and month from
     * @param [datepicker] - optional mat datepicker instance to close
     */
    setMonthAndYear(date: Date, datepicker?: MatDatepicker<Date>) {
        this.fromDateCtrl.setValue(startOfMonth(date));
        this.toDateCtrl.setValue(startOfDay(endOfMonth(date)));
        this.dateCtrl.setValue(date);
        datepicker?.close();
    }

    /** setQuarterAndYear - set from and to date to start and end of quarter for passed date
     * @param date - date to derive year and quarter from
     * @param [datepicker] - optional mat datepicker instance to close
     */
    setQuarterAndYear(date: Date, datepicker?: MatDatepicker<Date>) {
        this.fromDateCtrl.setValue(startOfQuarter(date));
        this.toDateCtrl.setValue(startOfDay(endOfQuarter(date)));
        this.dateCtrl.setValue(date);
        datepicker?.close();
    }

    /** setYear - set from and to date to start and end of year for passed date
     * @param date - date to derive year from
     * @param [datepicker] - optional mat datepicker instance to close
     */
    setYear(date: Date, datepicker?: MatDatepicker<Date>) {
        this.fromDateCtrl.setValue(startOfYear(date));
        this.toDateCtrl.setValue(startOfDay(endOfYear(date)));
        this.dateCtrl.setValue(date);
        datepicker?.close();
    }

    /** selectPrevious - select previous period
     * @param period - type of period to select `month | quarter | year`
     */
    selectPrevious(period: Exclude<(typeof this.periods)[number]['value'], 'custom'>) {
        const date = this.dateCtrl.value;
        if (period === 'month') {
            this.setMonthAndYear(subMonths(date, 1));
        } else if (period === 'quarter') {
            this.setQuarterAndYear(subQuarters(date, 1));
        } else if (period === 'year') {
            this.setYear(subYears(date, 1));
        }
    }
    /** selectNext - select next period
     * @param period - type of period to select `month | quarter | year`
     */
    selectNext(period: Exclude<(typeof this.periods)[number]['value'], 'custom'>) {
        const date = this.dateCtrl.value;
        if (period === 'month') {
            this.setMonthAndYear(addMonths(date, 1));
        } else if (period === 'quarter') {
            this.setQuarterAndYear(addQuarters(date, 1));
        } else if (period === 'year') {
            this.setYear(addYears(date, 1));
        }
    }

    ngOnInit(): void {
        combineLatest([
            this.fromDateCtrl.valueChanges.pipe(filterNilValue()),
            this.toDateCtrl.valueChanges.pipe(filterNilValue()),
            this.periodCtrl.valueChanges.pipe(startWith(this.periodCtrl.value))
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([fromDate, toDate, periodMode]) => {
                this.onChange({ fromDate, toDate, periodMode });
            });
        this.periodCtrl.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((period) => {
                const date = this.dateCtrl.value;
                if (period === 'month') {
                    this.setMonthAndYear(date);
                } else if (period === 'quarter') {
                    this.setQuarterAndYear(date);
                } else if (period === 'year') {
                    this.setYear(date);
                }
            });
    }
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
